import { useState } from 'react';
import { clearSavedCache } from '@/lib/persist.ts';
import { useQueryClient } from '@tanstack/react-query';
import type { HomeProfile } from '@shared/types.ts';
import { api } from '@/lib/api.ts';
import { keys, useHomeProfiles } from '@/lib/queries.ts';
import { Focusable } from '@/ui/Focusable.tsx';

/**
 * Pick who is watching.
 *
 * WHAT IS IN THIS LIST IS THE SERVER'S DECISION, and it is a narrow one: only
 * the owner of a Plex Home sees anything here, and only the managed profiles
 * inside it — the people with no Plex login of their own. Everybody who has
 * their own account signs in with it and never sees this screen. The client
 * does not filter; it draws what came back.
 *
 * A SWITCH RELOADS THE PAGE, and that is the fix rather than a shortcut.
 *
 * The first version cleared the query cache and carried on, and Continue
 * Watching kept showing the previous person until you navigated away and came
 * back. Clearing the query cache was never going to be enough: the player,
 * the lineup, now-playing, the view mode and the focus position all live in
 * Zustand stores that a query cache knows nothing about, and each of them
 * still held the last person's state. Chasing them one at a time would mean
 * finding out about the one that was missed from a family member.
 *
 * A switch is rare, deliberate, and means "this device is somebody else now".
 * A reload is the one operation that guarantees nothing survives it, and it
 * costs about a second — on the exact action where a second of loading is
 * expected.
 */
export function ProfilePicker({ onDone }: { onDone(): void }) {
  const { data } = useHomeProfiles();
  const qc = useQueryClient();
  const [pinFor, setPinFor] = useState<HomeProfile | null>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profiles = data?.profiles ?? [];

  async function enter(profile: HomeProfile, withPin?: string) {
    setBusy(true);
    setError(null);
    try {
      // Returning to the account that signed in needs no PIN and no round
      // trip to plex.tv — the session still holds its token.
      if (profile.isRoot) await api.switchBack();
      else await api.switchProfile(profile.id, withPin);

      qc.clear();
      clearSavedCache();
      // Straight to the top rather than reloading wherever they were: the
      // previous person may have been deep inside a library this profile
      // cannot see, and landing on a 404 is a poor first impression.
      window.location.assign('/');
    } catch (e) {
      // Stay on the keypad after a wrong PIN. Dropping back to the grid would
      // make a typo cost the whole journey back in.
      setError((e as Error).message);
      setPin('');
      setBusy(false);
    }
  }

  if (pinFor) {
    return (
      <Keypad
        title={pinFor.title}
        pin={pin}
        busy={busy}
        error={error}
        onDigit={(d) => setPin((p) => (p.length >= 6 ? p : p + d))}
        onBack={() => setPin((p) => p.slice(0, -1))}
        onCancel={() => { setPinFor(null); setPin(''); setError(null); }}
        onSubmit={() => { if (pin.length >= 4 && !busy) void enter(pinFor, pin); }}
      />
    );
  }

  return (
    <div className="profiles">
      {error && <div className="menu-note account-warn">{error}</div>}
      <div className="profile-grid">
        {profiles.map((p) => (
          <Focusable
            key={p.id}
            id={`profile-${p.id}`}
            className="profile"
            onSelect={() => {
              // Plex wants the PIN, so ask for it here rather than sending a
              // switch we already know will be refused. Not for the account
              // that signed in: going back to yourself is never challenged.
              if (p.needsPin && !p.isRoot) { setPinFor(p); setPin(''); setError(null); }
              else if (!busy) void enter(p);
            }}
          >
            <span className="profile-face">
              {p.avatarUrl
                ? <img src={p.avatarUrl} alt="" />
                : <span className="profile-initial">{p.title.slice(0, 1).toUpperCase()}</span>}
            </span>
            <span className="profile-name">{p.title}</span>
            {p.isRoot && <span className="profile-tag">signed in as</span>}
          </Focusable>
        ))}
      </div>
    </div>
  );
}

/**
 * Four digits, with a remote.
 *
 * An on-screen pad rather than a text input, because a Fire TV remote has no
 * number keys and a D-pad in a text field is the worst interaction in the
 * app. Ten buttons laid out like a phone is more markup and less work for
 * whoever is holding the remote, which is the trade worth making every time.
 *
 * The typed PIN is shown as dots and never echoed — somebody else is usually
 * in the room, and this is the one screen where that is the whole point.
 */
function Keypad({
  title, pin, busy, error, onDigit, onBack, onCancel, onSubmit,
}: {
  title: string; pin: string; busy: boolean; error: string | null;
  onDigit(d: string): void; onBack(): void; onCancel(): void; onSubmit(): void;
}) {
  return (
    <div className="profiles">
      <div className="keypad-head">
        <div className="big">{title}</div>
        <div className="muted">{error ?? 'Enter the Plex PIN for this profile'}</div>
        <div className="keypad-dots" aria-label={`${pin.length} digits entered`}>
          {'••••••'.slice(0, pin.length) || ' '}
        </div>
      </div>

      <div className="keypad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <Focusable key={d} id={`pin-${d}`} className="btn keypad-key" onSelect={() => onDigit(d)}>
            {d}
          </Focusable>
        ))}
        <Focusable id="pin-del" className="btn keypad-key keypad-aux" onSelect={onBack}>⌫</Focusable>
        <Focusable id="pin-0" className="btn keypad-key" onSelect={() => onDigit('0')}>0</Focusable>
        <Focusable
          id="pin-ok"
          className="btn keypad-key keypad-aux"
          onSelect={onSubmit}
        >
          {busy ? '…' : 'OK'}
        </Focusable>
      </div>

      <Focusable id="pin-cancel" className="btn menu-item" onSelect={onCancel}>
        Cancel
      </Focusable>
    </div>
  );
}
